import { useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useFileUpload } from '../hooks/useFileUpload';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../types/file.types';

interface FileDropzoneProps {
  onUploadComplete?: () => void;
  className?: string;
}

export const FileDropzone = ({
  onUploadComplete,
  className,
}: FileDropzoneProps) => {
  const { uploadFiles } = useFileUpload();

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Handle rejected files
      rejectedFiles.forEach((rejection) => {
        const errors = rejection.errors.map((e) => e.message).join(', ');
        toast.error(`"${rejection.file.name}": ${errors}`);
      });

      if (acceptedFiles.length === 0) return;

      const result = await uploadFiles(acceptedFiles);

      if (result.successful > 0 && onUploadComplete) {
        onUploadComplete();
      }
    },
    [uploadFiles, onUploadComplete]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: ALLOWED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 sm:p-8 transition-all cursor-pointer',
        isDragActive && !isDragReject && 'border-primary bg-primary/5 scale-[1.01]',
        isDragReject && 'border-destructive bg-destructive/5',
        !isDragActive && 'border-border hover:border-primary/50 hover:bg-accent/30',
        className
      )}
    >
      <input {...getInputProps()} />

      <div className="flex flex-col items-center gap-3 sm:gap-4 text-center">
        <div
          className={cn(
            'rounded-full p-3 sm:p-4 transition-colors',
            isDragActive && !isDragReject && 'bg-primary/10 text-primary',
            isDragReject && 'bg-destructive/10 text-destructive',
            !isDragActive && 'bg-muted text-muted-foreground'
          )}
        >
          {isDragReject ? (
            <X className="h-6 w-6 sm:h-8 sm:w-8" />
          ) : isDragActive ? (
            <FileText className="h-6 w-6 sm:h-8 sm:w-8" />
          ) : (
            <Upload className="h-6 w-6 sm:h-8 sm:w-8" />
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-sm sm:text-base font-medium text-foreground">
            {isDragReject
              ? 'Some files are not allowed'
              : isDragActive
                ? 'Drop files here'
                : 'Drag & drop files here'}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {isDragActive ? 'Release to upload' : 'or click to browse from your computer'}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (max 10MB)
          </p>
        </div>
      </div>
    </div>
  );
};
